import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { FaSitemap } from "react-icons/fa";
import { FaPlus } from "react-icons/fa6";
import { MdFormatListBulleted, MdBlock, MdDeleteOutline } from "react-icons/md";
import CrmListPage from "components/app/crm/shared/CrmListPage";
import SalesUnitFormDrawer from "./SalesUnitFormDrawer";
import { columnDefs as baseColumns } from "./component/Columns";
import {
  getAllSalesUnits,
  exportSalesUnits,
  deactivateSalesUnit,
  deleteSalesUnit
} from "redux/crm/salesUnitSlice";

const SORTABLE = {
  id: "id", name: "name", description: "description", region: "region", parent_id: "parent_id",
  sales_admin: "sales_admin", sales_leader: "sales_leader", sales_manager: "sales_manager",
  active: "active", type_of_sales: "type_of_sales", created_at: "created_at"
};

const columns = baseColumns.map((c) =>
  SORTABLE[c.field] ? { ...c, colId: SORTABLE[c.field], sortable: true } : { ...c, sortable: false }
);

const ADVANCED_FIELDS = [
  { key: "id",          label: "ID#",         type: "text", paramKey: "id_cont",          tagName: "ID#" },
  { key: "name",        label: "Name",        type: "text", paramKey: "name_cont",        tagName: "Name" },
  { key: "description", label: "Description", type: "text", paramKey: "description_cont", tagName: "Desc" },
  { key: "parent",      label: "Parent",      type: "text", paramKey: "parent_id_cont",  tagName: "Parent" }
];

const ACTIVE_OPTIONS = [
  { value: "", label: "All status" },
  { value: "true", label: "Active" },
  { value: "false", label: "Inactive" }
];

const TABS = [{ key: "all", label: "All", Icon: MdFormatListBulleted, thunk: getAllSalesUnits, dataKey: "allData" }];

const SalesUnitListPage = () => {
  const dispatch = useDispatch();
  const [showDrawer, setShowDrawer] = useState(false);
  const [reloadSignal, setReloadSignal] = useState(0);

  const newButton = (
    <button type="button" onClick={() => setShowDrawer(true)}
      className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-sm tw-border tw-border-primary-600 tw-bg-primary-600 tw-text-white hover:tw-bg-primary-700 tw-text-sm tw-font-medium tw-cursor-pointer">
      <FaPlus size={11} /> New Sales Unit
    </button>
  );

  const renderSelectionActions = (rows, refresh) => {
    const ids = rows.map((r) => r.id).filter(Boolean);
    const runAll = (thunk) => Promise.all(ids.map((id) => dispatch(thunk(id)))).then(refresh);

    return (
      <>
        <span className="tw-text-xs tw-text-slate-500 tw-mr-1">{ids.length} selected</span>
        <button type="button"
          onClick={() => { if (window.confirm(`Deactivate ${ids.length} sales unit(s)?`)) runAll(deactivateSalesUnit); }}
          className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-sm tw-border tw-border-amber-300 tw-bg-amber-50 tw-text-amber-700 hover:tw-bg-amber-100 tw-text-sm tw-font-medium tw-cursor-pointer">
          <MdBlock size={14} /> Deactivate
        </button>
        <button type="button"
          onClick={() => { if (window.confirm(`Delete ${ids.length} sales unit(s)? This cannot be undone.`)) runAll(deleteSalesUnit); }}
          className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-sm tw-border tw-border-red-300 tw-bg-red-50 tw-text-red-700 hover:tw-bg-red-100 tw-text-sm tw-font-medium tw-cursor-pointer">
          <MdDeleteOutline size={15} /> Delete
        </button>
      </>
    );
  };

  return (
    <>
      <CrmListPage
        title="Sales Units"
        subtitle="Configuration of sales units across regions."
        Icon={FaSitemap}
        reducerSelector={(s) => s.crm_sales_unit}
        tabs={TABS}
        columns={columns}
        defaultSort={{ attribute: "name", direction: "ASC" }}
        searchParamKey="id_or_name_or_description_cont"
        searchPlaceholder="ID#, name, description..."
        dateFilters={[{ key: "created", label: "Created date", paramKeys: ["created_date_gteq", "created_date_lteq"] }]}
        selects={[{ key: "active", paramKey: "active_eq", options: ACTIVE_OPTIONS }]}
        advancedFields={ADVANCED_FIELDS}
        advancedTitle="Filters for sales units"
        advancedDescription="Establish the sales unit's filtering criteria"
        exportThunk={exportSalesUnits}
        headerExtra={newButton}
        enableSelection
        renderSelectionActions={renderSelectionActions}
        reloadSignal={reloadSignal}
      />

      <SalesUnitFormDrawer
        show={showDrawer}
        onHide={() => setShowDrawer(false)}
        onSaved={() => setReloadSignal((n) => n + 1)}
      />
    </>
  );
};

export default SalesUnitListPage;
